import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { formatPrice } from "../../utils/format";
import type { ListingResponse } from "./types";

interface Props {
  listings: ListingResponse[];
  onClose: () => void;
}

interface RowDef {
  label: string;
  getValue: (l: ListingResponse) => string | number;
  format: (v: string | number) => string;
  highlightMax?: boolean;
}

function pricePerPyeong(l: ListingResponse): number {
  return l.area_pyeong > 0 ? Math.round(l.price_manwon / l.area_pyeong) : 0;
}

const ROW_DEFS: RowDef[] = [
  {
    label: "매물 유형",
    getValue: (l) => l.property_type,
    format: (v) => String(v),
  },
  {
    label: "지역",
    getValue: (l) =>
      `${l.region_sido} ${l.region_sigungu}${l.region_eupmyeondong ? " " + l.region_eupmyeondong : ""}`,
    format: (v) => String(v),
  },
  {
    label: "면적",
    getValue: (l) => l.area_pyeong,
    format: (v) => `${Number(v).toLocaleString()}평`,
    highlightMax: true,
  },
  {
    label: "매물가",
    getValue: (l) => l.price_manwon,
    format: (v) => formatPrice(Number(v)) + "원",
    highlightMax: true,
  },
  {
    label: "평당가",
    getValue: (l) => pricePerPyeong(l),
    format: (v) => (Number(v) > 0 ? `${Number(v).toLocaleString()}만원/평` : "-"),
  },
  {
    label: "지목",
    getValue: (l) => l.land_category || "-",
    format: (v) => String(v),
  },
  {
    label: "용도지역",
    getValue: (l) => l.zoning || "-",
    format: (v) => String(v),
  },
  {
    label: "도로접면",
    getValue: (l) => l.road_access || "-",
    format: (v) => String(v),
  },
];

/** 해당 행에서 최고값 인덱스 집합 반환 (동점 시 모두 포함) */
function getHighlightIdxs(row: RowDef, listings: ListingResponse[]): Set<number> {
  if (!row.highlightMax) return new Set();
  const vals = listings.map((l) => Number(row.getValue(l)));
  const max = Math.max(...vals);
  if (max <= 0) return new Set();
  const result = new Set<number>();
  vals.forEach((v, i) => { if (v === max) result.add(i); });
  return result;
}

export function CompareModal({ listings, onClose }: Props) {
  const m = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const st = compStyles(m);

  // ── 모바일: 매물 카드형 ──────────────────────────────
  if (m) {
    return (
      <div style={st.overlay} onClick={onClose}>
        <div style={st.mobileSheet} onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div style={st.header}>
            <h2 style={st.title}>매물 비교 ({listings.length}개)</h2>
            <button style={st.closeBtn} onClick={onClose}>{"✕"}</button>
          </div>
          {/* 카드 세로 스크롤 */}
          <div style={st.mobileScroll}>
            {listings.map((l, li) => (
              <div key={l.id} style={st.mobileCard}>
                <div style={st.mobileCardTop}>
                  <span style={st.mobileBadge}>{l.property_type}</span>
                  <span style={st.mobileNum}>#{li + 1}</span>
                </div>
                {ROW_DEFS.map((row) => {
                  const hiIdxs = getHighlightIdxs(row, listings);
                  const isHi = hiIdxs.has(li);
                  return (
                    <div key={row.label} style={{ ...st.mobileRow, ...(isHi ? st.mobileRowHi : {}) }}>
                      <span style={st.mobileLabel}>{row.label}</span>
                      <span style={{ ...st.mobileVal, ...(isHi ? st.hiText : {}) }}>
                        {row.format(row.getValue(l))}
                        {isHi && <span style={st.starBadge}> ★</span>}
                      </span>
                    </div>
                  );
                })}
                <button
                  style={st.detailBtn}
                  onClick={() => { onClose(); navigate(`/listings/${l.id}`); }}
                >
                  상세보기 →
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── 데스크톱: 비교 테이블 ──────────────────────────────
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={st.header}>
          <h2 style={st.title}>매물 비교 ({listings.length}개)</h2>
          <button style={st.closeBtn} onClick={onClose}>{"✕"}</button>
        </div>
        {/* 테이블 */}
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.thLabel} />
                {listings.map((l, li) => (
                  <th key={l.id} style={st.thListing}>
                    <span style={st.thBadge}>{l.property_type}</span>
                    <span style={st.thRegion}>
                      {l.region_sido} {l.region_sigungu}
                      {l.region_eupmyeondong ? ` ${l.region_eupmyeondong}` : ""}
                    </span>
                    <button
                      style={st.thDetailBtn}
                      onClick={() => { onClose(); navigate(`/listings/${l.id}`); }}
                    >
                      #{li + 1} 상세보기 →
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROW_DEFS.map((row, ri) => {
                const hiIdxs = getHighlightIdxs(row, listings);
                return (
                  <tr key={row.label} style={ri % 2 === 0 ? st.trEven : st.trOdd}>
                    <td style={st.tdLabel}>{row.label}</td>
                    {listings.map((l, li) => {
                      const isHi = hiIdxs.has(li);
                      return (
                        <td key={l.id} style={{ ...st.tdVal, ...(isHi ? st.tdHi : {}) }}>
                          {row.format(row.getValue(l))}
                          {isHi && <span style={st.starBadge}> ★</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// 스타일
// ═══════════════════════════════════════════

function compStyles(m: boolean): Record<string, React.CSSProperties> {
  const GOLD = "#D4A843";
  const NAVY = "#0F172A";
  const CARD_BG = "#1E293B";
  const BORDER = "rgba(212,168,83,0.25)";
  const HI_BG = "rgba(212,168,83,0.15)";

  return {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.82)",
      zIndex: 1000,
      display: "flex",
      alignItems: m ? "flex-end" : "center",
      justifyContent: "center",
      padding: m ? 0 : 24,
    },
    // ── 모바일 시트 ──
    mobileSheet: {
      background: NAVY,
      border: `1.5px solid ${BORDER}`,
      borderRadius: m ? "24px 24px 0 0" : 20,
      width: "100%",
      maxHeight: "90dvh",
      display: "flex",
      flexDirection: "column" as const,
      overflow: "hidden",
    },
    mobileScroll: {
      overflowY: "auto" as const,
      padding: "0 16px 32px",
      display: "flex",
      flexDirection: "column" as const,
      gap: 16,
    },
    mobileCard: {
      background: CARD_BG,
      border: `1px solid ${BORDER}`,
      borderRadius: 14,
      padding: "16px",
    },
    mobileCardTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 12,
    },
    mobileBadge: {
      display: "inline-block",
      padding: "3px 12px",
      borderRadius: 20,
      background: "rgba(212,168,83,0.2)",
      color: GOLD,
      fontSize: 13,
      fontWeight: 700,
    },
    mobileNum: {
      fontSize: 13,
      color: "rgba(255,255,255,0.35)",
      fontWeight: 600,
    },
    mobileRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "9px 0",
      borderBottom: `1px solid rgba(255,255,255,0.07)`,
    },
    mobileRowHi: {
      background: HI_BG,
      margin: "0 -16px",
      padding: "9px 16px",
      borderBottom: `1px solid rgba(212,168,83,0.15)`,
    },
    mobileLabel: {
      fontSize: 13,
      color: "rgba(255,255,255,0.5)",
      fontWeight: 500,
    },
    mobileVal: {
      fontSize: 13,
      color: "rgba(255,255,255,0.85)",
      fontWeight: 600,
      textAlign: "right" as const,
    },
    // ── 데스크톱 모달 ──
    modal: {
      background: NAVY,
      border: `1.5px solid ${BORDER}`,
      borderRadius: 20,
      width: "100%",
      maxWidth: 820,
      maxHeight: "88vh",
      display: "flex",
      flexDirection: "column" as const,
      overflow: "hidden",
      boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
    },
    // ── 공통 ──
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: m ? "18px 16px 14px" : "20px 28px 16px",
      borderBottom: `1px solid ${BORDER}`,
      flexShrink: 0,
    },
    title: {
      fontSize: m ? 17 : 19,
      fontWeight: 700,
      color: GOLD,
      margin: 0,
      letterSpacing: "-0.3px",
    },
    closeBtn: {
      background: "rgba(255,255,255,0.08)",
      border: "none",
      color: "rgba(255,255,255,0.6)",
      fontSize: 18,
      width: 32,
      height: 32,
      borderRadius: 8,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
      flexShrink: 0,
    },
    tableWrap: {
      overflowY: "auto" as const,
      overflowX: "auto" as const,
      flex: 1,
    },
    table: {
      width: "100%",
      borderCollapse: "collapse" as const,
      fontSize: 14,
    },
    thLabel: {
      width: 110,
      background: "rgba(255,255,255,0.04)",
      padding: "14px 20px",
      borderBottom: `2px solid ${BORDER}`,
      borderRight: `1px solid rgba(255,255,255,0.07)`,
    },
    thListing: {
      padding: "14px 20px",
      textAlign: "center" as const,
      borderBottom: `2px solid ${BORDER}`,
      borderRight: `1px solid rgba(255,255,255,0.07)`,
      verticalAlign: "top" as const,
    },
    thBadge: {
      display: "block",
      padding: "3px 12px",
      borderRadius: 20,
      background: "rgba(212,168,83,0.2)",
      color: GOLD,
      fontSize: 12,
      fontWeight: 700,
      marginBottom: 4,
    },
    thRegion: {
      display: "block",
      fontSize: 12,
      color: "rgba(255,255,255,0.55)",
      marginBottom: 8,
      lineHeight: 1.4,
    },
    thDetailBtn: {
      display: "inline-block",
      padding: "5px 12px",
      borderRadius: 8,
      border: `1px solid ${BORDER}`,
      background: "transparent",
      color: GOLD,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap" as const,
    },
    trEven: { background: "rgba(255,255,255,0.02)" },
    trOdd: { background: "transparent" },
    tdLabel: {
      padding: "12px 20px",
      color: "rgba(255,255,255,0.45)",
      fontSize: 13,
      fontWeight: 600,
      borderRight: `1px solid rgba(255,255,255,0.07)`,
      whiteSpace: "nowrap" as const,
    },
    tdVal: {
      padding: "12px 20px",
      color: "rgba(255,255,255,0.85)",
      fontSize: 14,
      fontWeight: 500,
      textAlign: "center" as const,
      borderRight: `1px solid rgba(255,255,255,0.07)`,
    },
    tdHi: {
      background: HI_BG,
      color: GOLD,
      fontWeight: 700,
    },
    hiText: { color: GOLD, fontWeight: 700 },
    starBadge: {
      color: GOLD,
      fontSize: 12,
      fontWeight: 700,
    },
    detailBtn: {
      marginTop: 14,
      width: "100%",
      padding: "10px 0",
      borderRadius: 10,
      border: `1px solid ${BORDER}`,
      background: "transparent",
      color: GOLD,
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
    },
  };
}
